// src/pages/system/RoleForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const RoleForm = ({ open, onClose, onSubmit, role = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissionsByModule, setPermissionsByModule] = useState({});

  useEffect(() => {
    if (open) {
      fetchPermissions();
    }
  }, [open]);

  useEffect(() => {
    if (open && role && permissions.length > 0) {
      // Asegurarnos de que role.permissions sea un array
      const rolePermissions = Array.isArray(role.permissions) ? role.permissions : [];

      // Mapear los códigos de permisos a IDs
      const permissionIds = permissions
        .filter(p => rolePermissions.includes(p.code))
        .map(p => p.id);

      setFormData({
        name: role.name,
        description: role.description,
        permissions: permissionIds
      });
    } else if (open && !role) {
      setFormData({
        name: '',
        description: '',
        permissions: []
      });
    }
  }, [open, role, permissions]);

  const fetchPermissions = async () => {
    try {
      const response = await fetch('/api/roles/permissions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al obtener permisos');
      }

      const data = await response.json();
      setPermissions(data);

      // Agrupar permisos por módulo
      const groupedPermissions = data.reduce((acc, permission) => {
        if (!acc[permission.module]) {
          acc[permission.module] = [];
        }
        acc[permission.module].push(permission);
        return acc;
      }, {});

      setPermissionsByModule(groupedPermissions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => {
      const currentPermissions = prev.permissions || [];
      const newPermissions = currentPermissions.includes(permissionId)
        ? currentPermissions.filter(p => p !== permissionId)
        : [...currentPermissions, permissionId];

      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  const handleSelectAllModulePermissions = (module, checked) => {
    const modulePermissionIds = permissionsByModule[module].map(p => p.id);
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? Array.from(new Set([...prev.permissions, ...modulePermissionIds]))
        : prev.permissions.filter(p => !modulePermissionIds.includes(p))
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) {
      setError('El nombre del rol es obligatorio');
      return;
    }
    onSubmit(formData);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box className="flex justify-center items-center p-8">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {role ? 'Editar Rol' : 'Nuevo Rol'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4">
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nombre del Rol"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
            />

            <Typography variant="h6" className="mt-6 mb-2">
              Permisos
            </Typography>

            {Object.entries(permissionsByModule).map(([module, modulePermissions]) => (
              <Accordion key={module}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box className="flex items-center justify-between w-full">
                    <Typography className="font-medium">{module}</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={modulePermissions.every(p =>
                            formData.permissions.includes(p.id)
                          )}
                          indeterminate={
                            modulePermissions.some(p =>
                              formData.permissions.includes(p.id)
                            ) &&
                            !modulePermissions.every(p =>
                              formData.permissions.includes(p.id)
                            )
                          }
                          onChange={(e) =>
                            handleSelectAllModulePermissions(module, e.target.checked)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label="Seleccionar todo"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {modulePermissions.map((permission) => (
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={formData.permissions.includes(permission.id)}
                            onChange={() => handlePermissionChange(permission.id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{permission.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {permission.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SecurityIcon />}
          >
            {role ? 'Actualizar Rol' : 'Crear Rol'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RoleForm;